import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import { calcClientDerivatives } from '@shared';
import { FundingPolicyType } from '@shared/appModel';
import { selectClient } from '@client/model/selectors';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSession } from '@/features/guided-financial-planning/context';
import { useAddPolicyFromQuote } from './use-add-policy-from-quote';
import {
  isTraditionalLTCQuote,
  getCarrierName,
  type GuidedPlanningQuote,
} from './use-quotes-display';

/**
 * Selected quote info structure from useAddPolicyFromQuote
 */
interface SelectedQuoteInfo {
  quoteId: string;
  quote: GuidedPlanningQuote;
  policyFundingSource: PendingPolicyFundingSource;
}

/**
 * Metrics for a single scenario (with or without insurance)
 */
export interface ScenarioMetrics {
  /** Self-funding projected coverage */
  projectedAssets: number;
  /** Policy coverage (0 for without insurance scenario) */
  policyCoverage: number;
  /** Combined coverage (self-funding + policy) */
  totalCoverage: number;
  /** Percentage of LTC cost covered */
  coveragePercent: number;
  /** Coverage gap: max(0, ltcCost - totalCoverage) */
  coverageGap: number;
  /** Surplus coverage: max(0, totalCoverage - ltcCost) */
  surplus: number;
}

/**
 * Information about the selected policy from the guided planning session
 */
export interface SelectedPolicyInfo {
  quoteId: string;
  productName: string;
  carrierName: string;
  policyType: FundingPolicyType | null;
  totalCost: number;
}

/**
 * Allocation from a single funding source toward the policy
 */
export interface FundingAllocation {
  selfFundingSourceId: string;
  accountName: string;
  amount: number;
  existingValue?: number;
}

/**
 * Benefit scenario analysis - categorizes the type of improvement insurance provides
 */
export interface InsuranceBenefitScenario {
  /** Insurance provides any meaningful benefit */
  insuranceIsBetter: boolean;
  /** Gap Reduction: Had gap without insurance, insurance reduces but doesn't eliminate it */
  hasGapReduction: boolean;
  /** Gap Elimination: Had gap without insurance, insurance eliminates it completely */
  hasGapElimination: boolean;
  /** Surplus Improvement: Already had surplus, insurance increases it further */
  hasSurplusImprovement: boolean;
}

/**
 * Result of comparing scenarios with and without insurance
 */
export interface PlanComparisonResult {
  /** Projected total LTC cost */
  projectedLtcCost: number;
  /** Metrics for self-funding only scenario */
  withoutInsurance: ScenarioMetrics;
  /** Metrics for self-funding + insurance scenario */
  withInsurance: ScenarioMetrics;
  /** Information about the selected policy */
  selectedPolicy: SelectedPolicyInfo | null;
  /** Funding allocations toward the policy */
  fundingAllocations: FundingAllocation[];
  /** Reduction in coverage gap: withoutInsurance.gap - withInsurance.gap */
  gapReduction: number;
  /** Net benefit in coverage */
  netBenefit: number;
  /** Benefit scenario analysis */
  benefitScenario: InsuranceBenefitScenario;
  /** Whether a policy is linked to the session */
  hasPolicy: boolean;
  /** Loading state from session */
  isLoading: boolean;
}

const DEFAULT_METRICS: ScenarioMetrics = {
  projectedAssets: 0,
  policyCoverage: 0,
  totalCoverage: 0,
  coveragePercent: 0,
  coverageGap: 0,
  surplus: 0,
};

/**
 * Derive the benefit scenario from a snapshot's metrics.
 * Used when returning cached snapshot data for completed sessions.
 */
function deriveBenefitScenarioFromSnapshot(
  snapshot: PlanComparisonSnapshot,
): InsuranceBenefitScenario {
  const withoutGap = snapshot.withoutInsurance.coverageGap;
  const withGap = snapshot.withInsurance.coverageGap;
  const { gapReduction, netBenefit } = snapshot;

  const insuranceIsBetter = gapReduction > 0 || netBenefit > 0;
  const hasGapReduction = withoutGap > 0 && withGap > 0 && withGap < withoutGap;
  const hasGapElimination = withoutGap > 0 && withGap === 0 && insuranceIsBetter;
  const hasSurplusImprovement = withoutGap === 0 && withGap === 0 && insuranceIsBetter;

  return {
    insuranceIsBetter,
    hasGapReduction,
    hasGapElimination,
    hasSurplusImprovement,
  };
}

/**
 * Extract scenario metrics from a calculated client object
 */
function extractMetrics(calculated: Client, projectedLtcCost: number): ScenarioMetrics {
  const selfFundingCoverage =
    calculated.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;
  const policyCoverage =
    calculated.multipleFundingSources?.multiplePolicyProjectedCostCoverage ?? 0;
  const totalCoverage = calculated.fundingSources?.combinedProjectedCostCoverage ?? 0;

  return {
    projectedAssets: selfFundingCoverage,
    policyCoverage,
    totalCoverage,
    coveragePercent:
      projectedLtcCost > 0 ? Math.round((totalCoverage / projectedLtcCost) * 100) : 0,
    coverageGap: Math.max(0, projectedLtcCost - totalCoverage),
    surplus: Math.max(0, totalCoverage - projectedLtcCost),
  };
}

/**
 * Helper to calculate all plan comparison data from client and session state.
 * When user did not select "Existing LTC" on assets step, the "Without Insurance"
 * scenario uses self-funding only (existing LTC policies excluded), matching Coverage Assessment.
 */
function calculatePlanComparison(
  client: Client | null,
  selectedQuoteInfo: SelectedQuoteInfo | null,
  allocations: Record<string, number>,
  userSelectedExistingLtcPolicy: boolean,
): Omit<PlanComparisonResult, 'isLoading'> {
  const projectedLtcCost = client?.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;

  // Calculate "Without Insurance" scenario
  let withoutInsurance = DEFAULT_METRICS;
  if (client) {
    const clientClone = cloneDeep(client);
    if (!userSelectedExistingLtcPolicy && clientClone.multipleFundingSources?.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }
    const calculated = calcClientDerivatives(clientClone, null);
    withoutInsurance = extractMetrics(calculated, projectedLtcCost);
  }

  // Calculate "With Insurance" scenario
  let withInsurance = withoutInsurance;
  let selectedPolicy: SelectedPolicyInfo | null = null;

  if (client && selectedQuoteInfo) {
    const clientClone = cloneDeep(client);
    if (!userSelectedExistingLtcPolicy && clientClone.multipleFundingSources?.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }

    // Inject the policy from stepData into the client clone
    const tempFundingSourceId = selectedQuoteInfo.policyFundingSource.pendingFundingSourceId;
    if (!clientClone.multipleFundingSources) {
      clientClone.multipleFundingSources = {
        selfFunding: {},
        ltcPolicy: {},
        annuity: {},
      };
    }
    if (!clientClone.multipleFundingSources.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }
    clientClone.multipleFundingSources.ltcPolicy[tempFundingSourceId] =
      selectedQuoteInfo.policyFundingSource;

    // Apply premium deductions
    Object.entries(allocations).forEach(([selfFundingSourceId, amount]) => {
      const source = clientClone.multipleFundingSources?.selfFunding?.[selfFundingSourceId];
      if (source && source.existingAssetsValue != null) {
        source.existingAssetsValue = Math.max(0, source.existingAssetsValue - amount);
      }
    });

    const calculated = calcClientDerivatives(clientClone, null);
    withInsurance = extractMetrics(calculated, projectedLtcCost);

    // Build selected policy info with calculated policyTotalCost
    const { quoteId, quote, policyFundingSource } = selectedQuoteInfo;
    const calculatedPolicy = calculated.multipleFundingSources?.ltcPolicy?.[tempFundingSourceId];
    selectedPolicy = {
      quoteId,
      productName: quote.productName ?? 'Selected Policy',
      carrierName: isTraditionalLTCQuote(quote) ? quote.carrier : getCarrierName(quote),
      policyType: policyFundingSource.policyType ?? null,
      totalCost: calculatedPolicy?.policyTotalCost ?? 0,
    };
  }

  // Build funding allocations list
  const selfFunding = client?.multipleFundingSources?.selfFunding ?? {};
  const fundingAllocations: FundingAllocation[] = Object.entries(allocations)
    .filter(([, amount]) => amount > 0)
    .map(([selfFundingSourceId, amount]) => {
      const source = selfFunding[selfFundingSourceId];
      return {
        selfFundingSourceId,
        accountName: source?.selfFundingName ?? source?.accountType ?? 'Unknown Account',
        amount,
        existingValue: source?.existingAssetsValue ?? 0,
      };
    });

  // Calculate comparison values
  const gapReduction = withoutInsurance.coverageGap - withInsurance.coverageGap;
  const netBenefit = withInsurance.totalCoverage - withoutInsurance.totalCoverage;

  // Calculate benefit scenario
  const withoutGap = withoutInsurance.coverageGap;
  const withGap = withInsurance.coverageGap;
  const insuranceIsBetter = gapReduction > 0 || netBenefit > 0;

  const benefitScenario: InsuranceBenefitScenario = {
    insuranceIsBetter,
    hasGapReduction: withoutGap > 0 && withGap > 0 && withGap < withoutGap,
    hasGapElimination: withoutGap > 0 && withGap === 0 && insuranceIsBetter,
    hasSurplusImprovement: withoutGap === 0 && withGap === 0 && insuranceIsBetter,
  };

  return {
    projectedLtcCost,
    withoutInsurance,
    withInsurance,
    selectedPolicy,
    fundingAllocations,
    gapReduction,
    netBenefit,
    benefitScenario,
    hasPolicy: selectedQuoteInfo !== null,
  };
}

/**
 * Hook that compares two scenarios for the plan step:
 * 1. Without Insurance - Self-funding only
 * 2. With Insurance - Self-funding (minus premium allocations) + session policy
 *
 * Reads the selected quote from session.stepData.selectedQuotes and injects
 * the policy into calculations without requiring database persistence.
 *
 * For completed sessions, returns the cached snapshot to ensure consistent
 * read-only display after finalization.
 */
export function usePlanComparisonScenarios(): PlanComparisonResult {
  const client = useSelector(selectClient);
  const { session, isLoading } = useSession();
  const { flags } = useFeatureFlags();
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();

  // Check for cached snapshot (used for completed sessions)
  const snapshot = session?.stepData?.planComparisonSnapshot;

  const userSelectedExistingLtcPolicy = Boolean(
    flags.existingLtcPolicyTypeOnCard &&
      (session?.stepData?.selectedAssets ?? []).includes('existing-ltc-policy'),
  );

  // Get allocations for the selected quote from stepData
  const allocations = useMemo(() => {
    if (!selectedQuoteInfo) return {};
    return (session?.stepData?.policyFundingAllocations?.[selectedQuoteInfo.quoteId] ??
      {}) as Record<string, number>;
  }, [session?.stepData?.policyFundingAllocations, selectedQuoteInfo]);

  // Calculate all metrics (or return snapshot for completed sessions)
  const result = useMemo((): PlanComparisonResult => {
    // For completed sessions with a snapshot, return the cached data
    if (session?.status === 'completed' && snapshot) {
      return {
        projectedLtcCost: snapshot.projectedLtcCost,
        withoutInsurance: snapshot.withoutInsurance,
        withInsurance: snapshot.withInsurance,
        selectedPolicy: snapshot.selectedPolicy,
        fundingAllocations: snapshot.fundingAllocations,
        gapReduction: snapshot.gapReduction,
        netBenefit: snapshot.netBenefit,
        benefitScenario: deriveBenefitScenarioFromSnapshot(snapshot),
        hasPolicy: snapshot.selectedPolicy !== null,
        isLoading: false,
      };
    }

    // Calculate fresh metrics
    return {
      ...calculatePlanComparison(
        client,
        selectedQuoteInfo,
        allocations,
        userSelectedExistingLtcPolicy,
      ),
      isLoading,
    };
  }, [
    session?.status,
    snapshot,
    session?.stepData?.selectedAssets,
    client,
    selectedQuoteInfo,
    allocations,
    userSelectedExistingLtcPolicy,
    flags.existingLtcPolicyTypeOnCard,
    isLoading,
  ]);

  return result;
}
